import {
  Team,
  TeamContext,
  TeamContextProps,
  useOrganization,
} from '@foxtrotplatform/platform-micro-site-core';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';

import { getUserDeveloperTeams, getUserTeamInfo } from '../api/user';
import { SELECTED_USER_TEAM_ID } from '../constants';

const initialTeamState = {
  setCurrentTeam: (team: Team) => ({ team }),
  refreshTeams: () => ({}),
};

export interface UserProviderProps {
  children?: React.ReactNode;
}

export const TeamProvider: FunctionComponent<UserProviderProps> = (
  props: UserProviderProps
): JSX.Element => {
  const { user } = useAuth();
  const { currentOrg } = useOrganization();
  const [userTeams, setUserTeams] = useState<Team[]>();
  const [currentTeam, setCurrentTeam] = useState<Team>();
  const [teamProps, setTeamProps] =
    useState<TeamContextProps>(initialTeamState);
  useEffect(() => {
    if (currentOrg) {
      getTeams(currentOrg.id);
    } else {
      setCurrentTeam(undefined);
    }
  }, [currentOrg]);

  useEffect(() => {
    if (user) {
      const teamProps: TeamContextProps = {
        currentTeam,
        refreshTeams,
        teams: userTeams,
        setCurrentTeam: setCurrentTeams,
      };
      setTeamProps(teamProps);
    } else {
      setTeamProps(initialTeamState);
    }
    if (currentTeam) {
      localStorage.setItem(SELECTED_USER_TEAM_ID, currentTeam.id);
    }
  }, [currentTeam, user]);

  const getTeams = async (orgId: string) => {
    const teams = await getUserDeveloperTeams(orgId);

    if (teams) {
      const currentTeamId = localStorage.getItem(SELECTED_USER_TEAM_ID);
      await hydrateCurrentTeamRoles(teams);
      setCurrentTeam(teams.find(({ id }) => id === currentTeamId) || teams[0]);
    }
  };

  const hydrateCurrentTeamRoles = async (teams: Team[]) => {
    const UserTeams = await getUserTeamInfo(currentOrg?.id as string);
    const teamListWithRole = teams.map((t) => {
      const matchingTeam = UserTeams.find((m) => m.id === t.id);
      if (matchingTeam) {
        t.role = matchingTeam.role;
      }
      return t;
    }) as Team[];

    if (teamListWithRole && teamListWithRole.length > 0) {
      setUserTeams(teamListWithRole);
    }
  };

  const setCurrentTeams = (team: Team): void => {
    if (userTeams && team && !userTeams?.find((t) => t.id === team.id)) {
      setUserTeams([team, ...userTeams]);
    }
    setCurrentTeam(team);
  };

  const refreshTeams = React.useCallback(async () => {
    if (currentOrg) {
      getTeams(currentOrg.id);
    }
  }, [currentOrg]);

  return (
    <TeamContext.Provider value={teamProps}>
      {props.children}
    </TeamContext.Provider>
  );
};
